import type { EntitlementStatus } from '../types';
import {
  fetchAppStoreEntitlement as fetchIosAppStoreEntitlement,
  getAppStoreSubscriptionForRedeem as getIosAppStoreSubscriptionForRedeem,
  manageAppStoreSubscription as manageIosAppStoreSubscription,
  purchaseAppStore as purchaseIosAppStore,
  PurchaseCancelledError,
  restoreAppStore as restoreIosAppStore,
} from './appStore';
import {
  fetchMacAppStoreEntitlement,
  getMacAppStoreSubscriptionForRedeem,
  isMacAppStorePurchasePlatform,
  manageMacAppStoreSubscription,
  purchaseMacAppStore,
  restoreMacAppStore,
} from './macAppStore';
import {
  fetchWindowsStoreEntitlement,
  getWindowsStoreSubscriptionForRedeem,
  isWindowsStorePurchasePlatform,
  manageWindowsStoreSubscription,
  purchaseWindowsStore,
  restoreWindowsStore,
} from './windowsAppStore';
import { isIosInAppPurchasePlatform } from './detect';

export { PurchaseCancelledError };

export function isAppStorePurchasePlatform(): boolean {
  return isIosInAppPurchasePlatform() || isMacAppStorePurchasePlatform();
}

export function isNativeStorePurchasePlatform(): boolean {
  return isAppStorePurchasePlatform() || isWindowsStorePurchasePlatform();
}

export async function fetchAppStoreEntitlementUnified(): Promise<EntitlementStatus> {
  if (isIosInAppPurchasePlatform()) return fetchIosAppStoreEntitlement();
  if (isMacAppStorePurchasePlatform()) return fetchMacAppStoreEntitlement();
  if (isWindowsStorePurchasePlatform()) return fetchWindowsStoreEntitlement();
  throw new Error('Store purchases are not available on this platform.');
}

export async function purchaseAppStoreUnified(): Promise<void> {
  if (isIosInAppPurchasePlatform()) return purchaseIosAppStore();
  if (isMacAppStorePurchasePlatform()) return purchaseMacAppStore();
  if (isWindowsStorePurchasePlatform()) return purchaseWindowsStore();
  throw new Error('Store purchases are not available on this platform.');
}

export async function restoreAppStoreUnified(): Promise<boolean> {
  if (isIosInAppPurchasePlatform()) return restoreIosAppStore();
  if (isMacAppStorePurchasePlatform()) return restoreMacAppStore();
  if (isWindowsStorePurchasePlatform()) return restoreWindowsStore();
  return false;
}

export async function manageAppStoreSubscriptionUnified(): Promise<void> {
  if (isIosInAppPurchasePlatform()) return manageIosAppStoreSubscription();
  if (isMacAppStorePurchasePlatform()) return manageMacAppStoreSubscription();
  if (isWindowsStorePurchasePlatform()) return manageWindowsStoreSubscription();
  throw new Error('Subscription management is not available on this platform.');
}

export async function getAppStoreSubscriptionForRedeemUnified(): Promise<{
  source: 'appstore' | 'microsoft';
  originalTransactionId?: string;
  storeSubscriptionId?: string;
  userCollectionId?: string;
} | null> {
  if (isIosInAppPurchasePlatform()) {
    const subscription = await getIosAppStoreSubscriptionForRedeem();
    if (!subscription) return null;
    return {
      source: 'appstore',
      originalTransactionId: subscription.originalTransactionId,
    };
  }
  if (isMacAppStorePurchasePlatform()) {
    const subscription = await getMacAppStoreSubscriptionForRedeem();
    if (!subscription) return null;
    return {
      source: 'appstore',
      originalTransactionId: subscription.originalTransactionId,
    };
  }
  if (isWindowsStorePurchasePlatform()) {
    const subscription = await getWindowsStoreSubscriptionForRedeem();
    if (!subscription) return null;
    return {
      source: 'microsoft',
      storeSubscriptionId: subscription.storeSubscriptionId,
      userCollectionId: subscription.userCollectionId,
    };
  }
  return null;
}
